import { App } from '../app/index.js';
import { withDb } from '../data/idb.js';
import { Sync } from '../sync/index.js';
import { lwDispatch, lwOn } from '../core/livewire.js';
import { OFFLINE_MODE_ENABLED } from '../core/config.js';

// Helper: unregister every SW and delete all CacheStorage entries
async function nukeSWAndCaches() {
    // Unregister all service workers (root + any leftovers)
    try {
        if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map(r => r.unregister()));
            console.log('[force-refetch] unregistered SWs:', regs.length);
        }
    } catch (e) {
        console.warn('[force-refetch] SW unregister failed', e);
    }

    // Delete *all* caches created by any SW or by the page
    try {
        if (window.caches && caches.keys) {
            const keys = await caches.keys();
            await Promise.all(keys.map(k => caches.delete(k)));
            console.log('[force-refetch] deleted caches:', keys);
        }
    } catch (e) {
        console.warn('[force-refetch] CacheStorage clear failed', e);
    }

    // Clear web storage too
    try { localStorage?.clear?.(); } catch { }
    try { sessionStorage?.clear?.(); } catch { }

    // Deep-nuke IndexedDB databases (if the API is supported)
    // You already clear your own stores; this is just a safety net.
    try {
        if ('databases' in indexedDB) {
            const dbs = await indexedDB.databases();
            await Promise.all(
                dbs.map(d => d?.name ? new Promise(res => {
                    const req = indexedDB.deleteDatabase(d.name);
                    req.onsuccess = req.onerror = req.onblocked = () => res();
                }) : Promise.resolve())
            );
            console.log('[force-refetch] deleted IDB databases');
        }
    } catch (e) {
        // Safari doesn’t support indexedDB.databases(); ignore.
    }
}


lwOn('checkin.badges.initial.fetched', async ({ badges = [] } = {}) => {

    // When offline mode disabled, do not store anything locally
    if (!OFFLINE_MODE_ENABLED) {

        lwDispatch('checkin.badges.initial.ready');
        await App.renderSyncStatus();

    } else {

        if (!badges.length) {
            lwDispatch('checkin.badges.initial.ready');
            await App.renderSyncStatus();
            return;
        }

        await withDb(async (db) => {
            const tx = db.transaction('badges', 'readwrite');
            const store = tx.store;

            for (const b of badges) {

                const existing = await store.get(b.uuid);

                // Keep ONLY local, unsynced logs in IDB
                const localPendingLogs = Array.isArray(existing?.local_scan_logs)
                    ? existing.local_scan_logs.filter(l => l?.local_scan === true && l?.created_at_ts != null)
                    : [];

                await store.put({
                    ...(existing || {}),
                    uuid: b.uuid,
                    name: b.name,
                    company: b.company ?? null,
                    enabled: !!b.enabled,
                    scan_count: Number.isFinite(b.scan_count) ? b.scan_count : (existing?.scan_count ?? 0),
                    is_allowed: b.is_allowed,
                    access_details: b.access_details,
                    local_scan_logs: localPendingLogs,
                    synced: localPendingLogs.length === 0,
                    updated_at: b.updated_at,
                });
            }

            await tx.done;
        });

        lwDispatch('checkin.badges.initial.ready');
        await App.renderSyncStatus();

    }
});

lwOn('checkin.badge.synced', async ({ badge_uuid, scan_count } = {}) => {
    if (!OFFLINE_MODE_ENABLED) return; // do not touch local when offline mode disabled
    if (!badge_uuid) return;

    await withDb(async (db) => {

        const badge = await db.get('badges', badge_uuid);

        if (!badge) return;

        badge.local_scan_logs = [];
        if (Number.isFinite(scan_count)) badge.scan_count = scan_count;
        badge.synced = true;

        await db.put('badges', badge);

    });

    await App.renderSyncStatus();
});

lwOn('checkin.badge.sync.failed', async ({ badge_uuid } = {}) => {
    if (!OFFLINE_MODE_ENABLED) return;
    if (!badge_uuid) return;
    await withDb(async (db) => {
        const badge = await db.get('badges', badge_uuid);
        if (!badge) return;
        badge.synced = false;
        await db.put('badges', badge);
    });
    await App.renderSyncStatus();
});

lwOn('checkin.badges.deleted', async ({ uuids = [] } = {}) => {
    if (!OFFLINE_MODE_ENABLED) return;
    if (!uuids.length) return;
    await withDb(async (db) => {
        const tx = db.transaction('badges', 'readwrite');
        for (const u of uuids) await tx.store.delete(u);
        await tx.done;
    });
    await App.renderSyncStatus();
});

lwOn('checkin.badges.force.refetch', async () => {

    // Close the modal
    window.closeModal?.('modal-checkin-settings');

    // Push anything unsynced BEFORE wiping local data (only when offline mode enabled)
    if (navigator.onLine && OFFLINE_MODE_ENABLED) {
        try {
            await withDb(db => Sync.pushUnsynced(db));
        } catch (e) {
            console.warn('pushUnsynced failed:', e);
        }
    }

    // Empty local DB (only when offline mode enabled)
    if (OFFLINE_MODE_ENABLED) {
        await withDb(async (db) => {
            const stores = Array.from(db.objectStoreNames || []);
            await Promise.all(
                stores.map((name) =>
                    db.clear(name).catch(err => console.warn(`clear ${name} failed`, err))
                )
            );
        });
    }

    // 3) Unregister SWs & clear all app caches
    await nukeSWAndCaches();

    // Hard reload with cache-busting param to trigger a clean install
    const u = new URL(location.href);
    u.searchParams.set('fresh', Date.now().toString());
    location.replace(u.toString());

});